import React from 'react'
import { AreaDisplayInfo } from '../../../Types/AreaDisplayInfo/AreaDisplayInfo'
import Card from '../../Evaluate/Card/Card'
import style from './CardCollection.module.css'
import { useWorkflowContext } from '../../../Hooks/useWorkflowContext'
import { ProductType } from '../../../Types/ProductType'
import Separator from '../Separator/Separator'
import { useLocation } from 'react-router-dom'

type CardCollectionProps = {
    archived: boolean,
    areas: AreaDisplayInfo[],
    template: boolean,
    toggleArchiveHandler?: (id: string) => void;
    toggleDisabledHandler?: (id: string) => void;
    disabledIds?: string[];
    titleChangedHandler?: (id: string, newValue: string) => void;
    productTypeChanged?: (type: ProductType, templateId: string) => void;
    showToggleSlider?: boolean;
    selectable?: boolean;
    selectedIds?: string[];
    onToggleSelect?: (id: string) => void;
    /** Tighter top spacing when a list header (e.g. export) sits above the grid */
    compactListTop?: boolean;
    hideExportButton?: boolean;
    onCardExport?: (id: string) => void;
}

const CardCollection = ({ archived, areas, template, toggleArchiveHandler, toggleDisabledHandler, disabledIds, titleChangedHandler, showToggleSlider, productTypeChanged, selectable, selectedIds, onToggleSelect, compactListTop, hideExportButton, onCardExport } : CardCollectionProps) => {
  const { getWorkflowPath, isAdminContext } = useWorkflowContext();
  const location = useLocation();
  
  // Check if we're on the standalone /template-editor route (not /portal/template-editor)
  const isStandaloneTemplateEditor = location.pathname.startsWith('/template-editor');

  // Check if there are active filters (any disabled IDs means filters are applied)
  const hasActiveFilters = disabledIds && disabledIds.length > 0;

  const handleTitleChange = (item: AreaDisplayInfo) => {
    return titleChangedHandler ? (newValue: string) => titleChangedHandler(item.id, newValue) : undefined;
  };

  const handleArchiveToggle = (item: AreaDisplayInfo) => {
    return toggleArchiveHandler ? () => toggleArchiveHandler(item.id) : undefined;
  };

  const handleDisabledToggle = (item: AreaDisplayInfo) => {
    return toggleDisabledHandler ? () => toggleDisabledHandler(item.id) : undefined;
  };

  // Separate areas into matching and non-matching
  const matchingAreas = areas.filter(area => !disabledIds?.includes(area.id));
  const nonMatchingAreas = areas.filter(area => disabledIds?.includes(area.id));

  const renderArea = (item: AreaDisplayInfo, isDisabled: boolean) => {
    let url = '';
    if (template) {
      // Determine the template type and route step based on product type
      let templateType = 'evaluate';
      let routeStep = 'define-fields';
      
      if (item.productType === ProductType.Improve) {
        templateType = 'improve';
        routeStep = 'improve-template-creation';
      } else if (item.productType === ProductType.Develop) {
        templateType = 'develop';
        routeStep = 'define-fields'; // Develop uses same workflow as Evaluate
      }
      
      // Use the workflow hook to generate the correct path
      url = getWorkflowPath(item.id, item.title, routeStep, templateType);
    } else {
      // For diagnostics/areas, route based on product type
      if (item.productType === ProductType.Develop) {
        url = `/develop/${item.id}`;
      } else if (item.productType === ProductType.Improve) {
        url = `/improve/${item.id}`;
      } else {
        url = `/evaluate/${item.id}`;
      }
    }
    
    // Only apply special classes if there are active filters
    let cardClass = '';
    if (hasActiveFilters) {
      cardClass = isDisabled ? style.disabledCard : style.matchingCard;
    }
    
    return (
      <div key={item.id} className={cardClass}>
        <Card 
          titleChangedHandler={handleTitleChange(item)} 
          toggleArchiveHandler={handleArchiveToggle(item)} 
          toggleDisabledHandler={handleDisabledToggle(item)} 
                  productTypeChanged={productTypeChanged}
          url={url} 
          areaDisplayInfo={item} 
          archived={archived} 
          template={template} 
          showToggleSlider={showToggleSlider}
          selectable={selectable}
          selected={selectedIds?.includes(item.id)}
          onToggleSelect={onToggleSelect}
          hideExportButton={hideExportButton}
          onCardExport={onCardExport ? () => onCardExport(item.id) : undefined}
        />
      </div>
    );
  };

  return (
    <div className={`${style.cardsContainer}${compactListTop ? ` ${style.cardsContainerListHeader}` : ''}`}>
      {/* Render matching areas */}
      {matchingAreas.map(item => renderArea(item, false))}
      
      {/* Add separator if there are both matching and non-matching areas */}
      {hasActiveFilters && matchingAreas.length > 0 && nonMatchingAreas.length > 0 && (
        <Separator text="No matches found" />
      )}
      
      {/* Render non-matching areas */}
      {nonMatchingAreas.map(item => renderArea(item, true))}
    </div>
  )
}

export default CardCollection